import { isRouteErrorResponse } from 'react-router'
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useRouteError, data } from 'react-router'

import type { Route } from './+types/root'
import * as styles from './root.css'

import { LayoutFooter } from '~/components/common/LayoutFooter'
import { LayoutHeader } from '~/components/common/LayoutHeader'
import { LayoutPopupMessage } from '~/components/common/LayoutPopupMessage'
import { LayoutPortal } from '~/components/common/LayoutPortal'
import { LayoutSideMenu } from '~/components/common/LayoutSideMenu'
import { LayoutWrapper } from '~/components/common/LayoutWrapper'
import { ErrorPage } from '~/components/ui/errors/ErrorPage'
import { AppProvider } from '~/providers/AppProvider'
import { useNonce } from '~/providers/NonceProvider'
import {
  createCsrfCookieHeader,
  generateCsrfToken,
  getCsrfTokenFromCookie,
  validateCsrfRequest,
} from '~/server/csrf.server'
import { getRootLayoutData } from '~/server/layout/root-layout-loader.server'
import { siteName } from '~/utils/meta'

const CommonMeta = () => {
  return (
    <>
      <meta charSet="utf-8" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, minimum-scale=1.0, shrink-to-fit=no, viewport-fit=cover"
      />
      <meta name="theme-color" content="#3bd986" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="author" content="株式会社eiicon" />
      <meta name="reply-to" content="info@eiicon.net" />
      <meta property="fb:app_id" content="1889101841333228" />
      <meta name="twitter:site" content="@auba_oi" />
      <link rel="icon" href="/favicon.ico" type="image/x-icon" sizes="16x16" />
      <link rel="apple-touch-icon" href="/apple-icon.png" type="image/png" sizes="180x180" />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Bilbo&display=swap" rel="stylesheet" />
    </>
  )
}

export const middleware: Route.MiddlewareFunction[] = [
  async ({ request }, next) => {
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(request.method)) {
      await validateCsrfRequest(request)
    }
    return next()
  },
]

export async function loader(args: Route.LoaderArgs) {
  const { request } = args
  const { headerInfo, sideMenuInfo, clearTokenCookie } = await getRootLayoutData(request)

  const existingCsrfToken = getCsrfTokenFromCookie(request)
  const csrfToken = existingCsrfToken || generateCsrfToken()

  const headers = new Headers()
  if (!existingCsrfToken) {
    headers.append('Set-Cookie', createCsrfCookieHeader(csrfToken))
  }
  if (clearTokenCookie) {
    headers.append('Set-Cookie', clearTokenCookie)
  }

  return data(
    {
      headerInfo,
      sideMenuInfo,
      csrfToken,
    },
    {
      headers,
    }
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { headerInfo, sideMenuInfo } = loaderData
  const nonce = useNonce()

  return (
    <AppProvider>
      <html lang="ja">
        <head>
          <CommonMeta />
          <Meta />
          <Links />
        </head>
        <body>
          <div className={styles.layoutRoot}>
            <div className={styles.layoutRoot_container}>
              <div
                className={[styles.layoutRootSide, sideMenuInfo && styles.layoutRootSide__active]
                  .filter(Boolean)
                  .join(' ')}
              >
                {sideMenuInfo && <LayoutSideMenu project={sideMenuInfo.project} />}
              </div>
              <div
                className={[styles.layoutRootMain, sideMenuInfo && styles.layoutRootMain__side]
                  .filter(Boolean)
                  .join(' ')}
              >
                <LayoutWrapper>
                  <LayoutHeader user={headerInfo.user} />
                  <main className={styles.layoutRootMain_container}>
                    <Outlet />
                  </main>
                  <LayoutFooter />
                </LayoutWrapper>
              </div>
            </div>
            <LayoutPortal />
            <LayoutPopupMessage />
          </div>
          <ScrollRestoration nonce={nonce || undefined} />
          <Scripts nonce={nonce || undefined} />
        </body>
      </html>
    </AppProvider>
  )
}

export function ErrorBoundary() {
  const nonce = useNonce()
  const error = useRouteError()
  const title = isRouteErrorResponse(error)
    ? error.status === 404
      ? 'ページが見つかりませんでした'
      : error.statusText
    : error instanceof Error
      ? 'エラーが発生しました'
      : '予期せぬエラーが発生しました'
  const description = isRouteErrorResponse(error)
    ? error.status === 404
      ? 'お探しのページは見つかりませんでした。'
      : error.statusText
    : error instanceof Error
      ? error.message
      : '予期せぬエラーが発生しました。'

  return (
    <html
      lang="ja"
      style={{
        background: '#f2f2f2',
        color: '#2f3a3a',
        blockSize: '100%',
        lineHeight: 1,
        inlineSize: '100%',
        boxSizing: 'border-box',
      }}
    >
      <head>
        <CommonMeta />
        <title>{`${title} | ${siteName}`}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="noindex, nofollow" />
        <Meta />
        <Links />
      </head>
      <body
        style={{
          backgroundAttachment: 'fixed',
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundColor: '#f2f2f2',
          borderBlock: 0,
          borderInline: 0,
          fontFamily:
            '"游ゴシック Medium", "Yu Gothic", YuGothic, YuGothicMedium, "Hiragino Kaku Gothic ProN", Meiryo, sans-serif',
          fontWeight: 500,
          inlineSize: '100%',
          lineHeight: 1.6,
          marginBlock: 0,
          marginInline: 0,
          overflow: 'clip auto',
          paddingBlock: 0,
          paddingInline: 0,
          WebkitTextSizeAdjust: '100%',
          textSizeAdjust: '100%',
          boxSizing: 'border-box',
        }}
      >
        <ErrorPage />
        <Scripts nonce={nonce || undefined} />
      </body>
    </html>
  )
}
